import { useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import BPAFindingSummaryCard from './BPAFindingSummaryCard';
import {
  ignoreBPAFindingItems,
  ignoreBPAFindings,
} from '../../services/bpaFindingsService';
import { findingsDataRefreshAtom } from '../../state';
import {
  BPAFinding,
  BPAIgnorePayload,
  BPAIgnoreScope,
} from '../../types/bpaFindings.types';

interface BPAFindingIgnoreModalProps {
  finding?: BPAFinding;
  isOpen: boolean;
  toggle: () => void;
  isIgnoreAction?: boolean;
}

const IGNORE_MESSAGE_MAX_LENGTH = 500;

const BPAFindingIgnoreModal: React.FC<BPAFindingIgnoreModalProps> = ({
  finding,
  isOpen,
  toggle,
  isIgnoreAction,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);
  const isMountedRef = useRef(true);

  const [selectedScope, setSelectedScope] =
    useState<BPAIgnoreScope>('single_finding');
  const [ignoredMessage, setIgnoredMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const shouldIgnore =
    typeof isIgnoreAction === 'boolean'
      ? isIgnoreAction
      : finding?.user_status !== 'Ignored';

  const findingIds = useMemo(
    () => (finding?.id ? [finding.id] : []),
    [finding?.id],
  );

  const actionLabel = shouldIgnore ? 'Ignore' : 'Unignore';
  const actionNoun = 'BPA Finding';
  const trimmedIgnoredMessage = ignoredMessage.trim();
  const commentPlaceholder = shouldIgnore
    ? 'Accepted risk, false positive, or business context'
    : 'Reason for restoring this finding';
  const scopeOptions: {
    value: BPAIgnoreScope;
    label: string;
    description: string;
  }[] = useMemo(
    () => [
      {
        value: 'single_finding',
        label: `Single ${actionLabel}`,
        description: `${actionLabel} selected instance of this finding.`,
      },
      {
        value: 'bulk_finding',
        label: `Bulk ${actionLabel}`,
        description: `${actionLabel} all instances of this finding for this integration.`,
      },
    ],
    [actionLabel],
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedScope('single_finding');
    setIgnoredMessage('');
    setError('');
  }, [isOpen]);

  const handleSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    event.preventDefault();

    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before updating findings.');
      return;
    }

    if (findingIds.length === 0) {
      showFailureToast('No BPA findings selected.');
      return;
    }

    if (!trimmedIgnoredMessage) {
      const validationMessage =
        'Comments are required before updating BPA finding status.';
      setError(validationMessage);
      showFailureToast(validationMessage);
      return;
    }

    const payload: BPAIgnorePayload = {
      company_id: companyId,
      finding_ids: findingIds,
      is_ignored: shouldIgnore,
      ignored_message: trimmedIgnoredMessage,
    };

    setLoading(true);
    setError('');

    let shouldClose = false;

    try {
      const response =
        selectedScope === 'bulk_finding'
          ? await ignoreBPAFindings(payload)
          : await ignoreBPAFindingItems(payload);

      if (!isMountedRef.current) return;

      showSuccessToast(
        response.message ||
          `${findingIds.length} BPA finding${
            findingIds.length === 1 ? '' : 's'
          } ${shouldIgnore ? 'ignored' : 'unignored'}.`,
      );
      refreshFindingsGridData(new Date().toISOString());
      shouldClose = true;
    } catch (err) {
      if (!isMountedRef.current) return;

      const failureMessage = extractApiErrorMessage(
        err,
        `Failed to ${shouldIgnore ? 'ignore' : 'unignore'} BPA findings.`,
      );
      setError(failureMessage);
      showFailureToast(failureMessage);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        if (shouldClose) {
          toggle();
        }
      }
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        {actionLabel} {actionNoun}
      </ModalHeader>
      <ModalBody className="pb-2">
        {finding ? <BPAFindingSummaryCard rowData={finding} /> : null}

        <Card className="mt-2 mb-0">
          <CardBody className="pt-3 pb-2">
            <Row>
              <Col md="12">
                <label>
                  <strong>{actionLabel} Options</strong>
                </label>
              </Col>
            </Row>
            {scopeOptions.map((option) => (
              <Row key={option.value} className="mb-2">
                <Col md="12">
                  <label
                    className="d-flex align-items-start mb-0"
                    htmlFor={`bpa_ignore_scope_${option.value}`}
                  >
                    <Input
                      id={`bpa_ignore_scope_${option.value}`}
                      type="radio"
                      name="bpa-ignore-scope"
                      className="mt-1 mr-2"
                      checked={selectedScope === option.value}
                      onChange={() => setSelectedScope(option.value)}
                      disabled={loading}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      <span className="d-block text-muted">
                        {option.description}
                      </span>
                    </span>
                  </label>
                </Col>
              </Row>
            ))}

            <Row className="mt-3">
              <Col md="12">
                <label htmlFor="bpa_ignore_message">
                  <strong>Comments</strong>
                  <span className="text-danger"> *</span>
                </label>
                <Input
                  id="bpa_ignore_message"
                  type="textarea"
                  rows="3"
                  maxLength={IGNORE_MESSAGE_MAX_LENGTH}
                  value={ignoredMessage}
                  onChange={(event) => setIgnoredMessage(event.target.value)}
                  placeholder={commentPlaceholder}
                  disabled={loading}
                  required
                  aria-required="true"
                />
                <div
                  className={`text-right small mt-1 ${
                    ignoredMessage.length >= IGNORE_MESSAGE_MAX_LENGTH
                      ? 'text-warning'
                      : 'text-muted'
                  }`}
                  aria-live="polite"
                >
                  {ignoredMessage.length}/{IGNORE_MESSAGE_MAX_LENGTH}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {error && <div className="text-danger mt-2">{error}</div>}
      </ModalBody>
      <ModalFooter className="pt-0">
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            onClick={(event) => void handleSubmit(event)}
            disabled={findingIds.length === 0 || !trimmedIgnoredMessage}
          >
            {actionLabel}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default BPAFindingIgnoreModal;
